import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useModes } from '@/composables/useModes';
import { isSharpCharts2DateStringOlderThanMinimumAllowed } from '@/composables/isSharpCharts2DateStringOlderThanMinimumAllowed';
import { isValidSharpCharts2DateString } from '@/utils/isValidSharpCharts2DateString';
import { isAnHtmlUIElementFocused } from '@/utils/isAnHtmlUIElementFocused';
import { RANGE_OPTION_SELECT_START_END } from '@/enums/range-options';
import { PERIOD_OPTION_WEEKLY, PERIOD_OPTION_MONTHLY, PERIOD_OPTION_QUARTERLY, PERIOD_OPTION_YEARLY } from '@/enums/period-options';
import { MIN_ALLOWED_DATE_STRING } from '@/enums/min-allowed-date-string';

const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_RANGE_SLIDER_DATE = 'today';

const callbacks = {};

const util = {
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  monthsVerbose: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  daysVerbose: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  addYears: function (date, nYears) {
    const y = date.getFullYear() + nYears;
    const m = date.getMonth() + 1;
    const daysInMonth = this.getDaysInMonth(y, m);
    const d = Math.min(date.getDate(), daysInMonth);

    return new Date(y, m - 1, d);
  },
  addMonths: function (date, nMonths) {
    let y = date.getFullYear();
    let m = date.getMonth() + 1;
    let d = date.getDate();

    while (nMonths > 0) {
      if (m === 12) {
        y++;
        m = 1;
      } else {
        m++;
      }

      nMonths--;
    }

    const daysInMonth = this.getDaysInMonth(y, m);

    if (d > daysInMonth) {
      d = daysInMonth;
    }

    return new Date(y, m - 1, d);
  },
  addDays: function (date, nDays) {
    let y = date.getFullYear();
    let m = date.getMonth() + 1;
    let d = date.getDate();

    while (nDays > 0) {
      if (d === 31 && m === 12) {
        y++;
        m = 1;
        d = 1;
      } else if (d === this.getDaysInMonth(y, m)) {
        m++;
        d = 1;
      } else {
        d++;
      }

      nDays--;
    }

    return new Date(y, m - 1, d);
  },
  subtractYears: function (date, nYears) {
    const y = date.getFullYear() - nYears;
    const m = date.getMonth() + 1;
    const daysInMonth = this.getDaysInMonth(y, m);
    const d = Math.min(date.getDate(), daysInMonth);

    return new Date(y, m - 1, d);
  },
  subtractMonths: function (date, nMonths) {
    let y = date.getFullYear();
    let m = date.getMonth() + 1;
    let d = date.getDate();

    while (nMonths > 0) {
      if (m === 1) {
        y--;
        m = 12;
      } else {
        m--;
      }

      nMonths--;
    }

    const daysInMonth = this.getDaysInMonth(y, m);

    if (d > daysInMonth) {
      d = daysInMonth;
    }

    return new Date(y, m - 1, d);
  },
  subtractDays: function (date, nDays) {
    let y = date.getFullYear();
    let m = date.getMonth() + 1;
    let d = date.getDate();

    while (nDays > 0) {
      if (d === 1 && m === 1) {
        y--;
        m = 12;
        d = this.getDaysInMonth(y, m);
      } else if (d === 1) {
        m--;
        d = this.getDaysInMonth(y, m);
      } else {
        d--;
      }

      nDays--;
    }

    return new Date(y, m - 1, d);
  },
  getDaysInMonth: function (y, m) {
    return [31, this.isLeapYear(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31].at(m - 1);
  },
  isLeapYear: function (y) {
    return (y % 100 !== 0 && y % 4 === 0) || y % 400 === 0;
  },
  pad: function (num) {
    return num < 10 ? '0' + num : num;
  }
}

function bind(func, context) {
  return function () {
    return func.apply(context, arguments);
  }
}

const pubsub = {
  emit: function (topic) {
    // example: emit('some-topic', arg1, arg2, arg3, ...);

    const args = Array.prototype.slice.call(arguments, 1);

    if (!Array.isArray(callbacks[topic])) {
      return;
    }

    for (const callback of callbacks[topic]) {
      callback.apply(this, args);
    }
  },
  on: function (topic, callback, context) {
    // example: on('some-topic', function (arg1, arg2, arg3) {});

    if (!callbacks[topic]) {
      callbacks[topic] = [];
    }

    if (context) {
      callback = bind(callback, context);
    }

    callbacks[topic].push(callback);

    return this;
  }
};

class TimeStamp {
  reMMMDYYYY = /^(\D+)(\d+),(19\d{2}|20\d{2})$/; // mmm d, yyyy (like: Jun 6, 2014)
  reDMMMYYYY = /^(\d+)[-]*(\D+)[-,]*(19\d{2}|20\d{2})$/; // d mmm yyyy (like: 6 Jun 2014)
  reMMMYYYY = /^(\D+)[-,]*(19\d{2}|20\d{2})$/; // mmm yyyy (like: Jun 2014)
  reString = /^(\D+)$/; // string (like: 'today', 'now')
  reYYYYMD = /^(19\d{2}|20\d{2})[-/](\d+)[-/](\d+)$/; // yyyy/m/d or yyyy-m-d
  reMDYYYY = /^(\d+)[-/](\d+)[-/](19\d{2}|20\d{2})$/; // m/d/yyyy or m-d-yyyy
  reMDYY = /^(\d+)[-/](\d+)[-/](\d{2})$/; // m/d/yy or m-d-yy
  reYYYYMMDD = /^(19\d{2}|20\d{2})(\d{2})(\d{2})$/; // yyyymmdd
  reYYYY = /^(19\d{2}|20\d{2})$/; // yyyy
  reMillis = /^(-?\d+)\.?\d*$/; // millis
  reYearsAgo = /^(\d+)years?ago$/; // years ago
  reMonthsAgo = /^(\d+)months?ago$/; // months ago
  reDaysAgo = /^(\d+)days?ago$/; // days ago

  constructor() {
    this.date = null;
    this.isNull = false;

    if (!arguments.length) {
      // current time
      this.date = new Date();
    } else if (arguments.length === 1) {
      this.date = this.parse(arguments[0]);
    } else if (arguments.length === 3) {
      // month, day, year
      this.date = new Date(arguments[2], arguments[0] - 1, arguments[1]);
    }

    if (this.date) {
      this.date.setHours(0);
      this.date.setMinutes(0);
      this.date.setSeconds(0);
      this.date.setMilliseconds(0);
    }
  }

  parse(str) {
    // first, get rid of all spaces
    str = str.toString().toLowerCase().replace(/\s/g, '');

    if (!str.length) {
      return new Date();
    }

    // yyyy/m/d or yyyy-m-d
    let arr = str.match(this.reYYYYMD);

    if (arr) {
      return new Date(arr[1], arr[2] - 1, arr[3]);
    }

    // m/d/yyyy or m-d-yyyy
    arr = str.match(this.reMDYYYY);

    if (arr) {
      return new Date(arr[3], arr[1] - 1, arr[2]);
    }

    // m/d/yy or m-d-yy
    arr = str.match(this.reMDYY);

    if (arr) {
      let yr = parseInt(arr[3]);

      if (yr <= (new Date().getFullYear() - 2000)) {
        yr += 2000;
      } else {
        yr += 1900;
      }

      return new Date(yr, arr[1] - 1, arr[2]);
    }

    // yyyymmdd
    arr = str.match(this.reYYYYMMDD);

    if (arr) {
      return new Date(arr[1], arr[2] - 1, arr[3]);
    }

    // yyyy
    arr = str.match(this.reYYYY);

    if (arr) {
      return new Date(arr[1], 0, 1);
    }

    // mmm d, yyyy (like: Jun 6, 2014)
    arr = str.match(this.reMMMDYYYY);

    if (arr) {
      const year = arr[3];
      const month = arr[1];
      const day = arr[2];

      for (let i = 0; i < util.months.length; i++) {
        if (
          util.months[i].toLowerCase() === month ||
          util.monthsVerbose[i].toLowerCase() === month
        ) {
          return new Date(year, i, day);
        }
      }
    }

    // d mmm yyyy (like: 6 Jun 2014)
    arr = str.match(this.reDMMMYYYY);

    if (arr) {
      const year = arr[3];
      const month = arr[2];
      const day = arr[1];

      for (let i = 0; i < util.months.length; i++) {
        if (
          util.months[i].toLowerCase() === month ||
          util.monthsVerbose[i].toLowerCase() === month
        ) {
          return new Date(year, i, day);
        }
      }
    }

    // mmm yyyy (like: Jun 2014)
    arr = str.match(this.reMMMYYYY);

    if (arr) {
      const year = arr[2];
      const month = arr[1];
      const day = 1;

      for (let i = 0; i < util.months.length; i++) {
        if (
          util.months[i].toLowerCase() === month ||
          util.monthsVerbose[i].toLowerCase() === month
        ) {
          return new Date(year, i, day);
        }
      }
    }

    // millis
    arr = str.match(this.reMillis);

    if (arr) {
      return new Date(parseInt(arr[1]));
    }

    // string
    arr = str.match(this.reString);

    if (arr) {
      if (
        arr[1].indexOf('today') !== -1 ||
        arr[1].indexOf('now') !== -1
      ) {
        return new Date();
      }
    }

    // n years ago
    arr = str.match(this.reYearsAgo);

    if (arr) {
      const nYearsAgo = arr[1];

      return util.subtractYears(new Date(), nYearsAgo);
    }

    // n months ago
    arr = str.match(this.reMonthsAgo);

    if (arr) {
      const nMonthsAgo = arr[1];

      return util.subtractMonths(new Date(), nMonthsAgo);
    }

    // n days ago
    arr = str.match(this.reDaysAgo);

    if (arr) {
      const nDaysAgo = arr[1];

      return util.subtractDays(new Date(), nDaysAgo);
    }

    // can't parse
    this.isNull = true

    return new Date();
  }

  format(pattern) {
    // September 1, 2014
    // YYYY 2014
    // YY   14
    // MMMM September
    // MMM  Sep
    // MM   09
    // M    9
    // DD   01
    // D    1
    // EEEE Monday
    // EEE  Mon
    // EE   02
    // E    2
    let result = pattern.replace(/YYYY/g, this.getYear());
    result = result.replace(/YY/g, this.getYear().toString().substr(-2));
    result = result.replace(/MMMM/g, util.monthsVerbose[this.date.getMonth()]);
    result = result.replace(/MMM/g, util.months[this.date.getMonth()]);
    result = result.replace(/MM/g, util.pad(this.getMonth()));
    result = result.replace(/M/g, this.getMonth());
    result = result.replace(/DD/g, util.pad(this.getDay()));
    result = result.replace(/D/g, this.getDay());
    result = result.replace(/EEEE/g, util.daysVerbose[this.date.getDay()]);
    result = result.replace(/EEE/g, util.days[this.date.getDay()]);
    result = result.replace(/EE/g, util.pad(this.date.getDay()));
    result = result.replace(/E/g, this.date.getDay());
    // we may have replaced valid 'M' and 'D' characters, clean up
    result = result.replace(/\d+ar/g, 'Mar');
    result = result.replace(/\d+ay/g, 'May');
    result = result.replace(/\d+on/g, 'Mon');
    result = result.replace(/\d+ec/g, 'Dec');

    return result;
  }

  getYear() {
    return this.date.getFullYear();
  }

  getMonth() {
    return this.date.getMonth() + 1;
  }

  getDay() {
    return this.date.getDate();
  }

  getDate() {
    return this.date;
  }

  getMillis() {
    return this.date.getTime();
  }

  gt(inTimeStamp) {
    return this.getMillis() > inTimeStamp.getMillis();
  }

  ge(inTimeStamp) {
    return this.getMillis() >= inTimeStamp.getMillis();
  }

  lt(inTimeStamp) {
    return this.getMillis() < inTimeStamp.getMillis();
  }

  le(inTimeStamp) {
    return this.getMillis() <= inTimeStamp.getMillis();
  }

  eq(inTimeStamp) {
    return this.getMillis() === inTimeStamp.getMillis();
  }

  isToday() {
    const today = new Date();

    return (
      this.getYear() === today.getFullYear() &&
      this.getMonth() === today.getMonth() + 1 &&
      this.getDay() === today.getDate()
    );
  }

  isWeekend() {
    return (
      this.date.getDay() === 6 ||
      this.date.getDay() === 0
    );
  }

  isWeekday() {
    return !this.isWeekend();
  }

  isHoliday() {
    // Christmas and New Year's are the only holidays we know about
    const isNewYears  = (
      (this.getMonth() === 1 && this.getDay() === 1 && this.isWeekday()) ||
      (this.getMonth() === 1 && this.isMonday() && (this.getDay() === 2 || this.getDay() === 3))
    );
    const isChristmas = (
      (this.getMonth() === 12 && this.getDay() === 25 && this.isWeekday()) ||
      (this.getMonth() === 12 && this.isMonday() && (this.getDay() === 26 || this.getDay() === 27))
    );

    return isNewYears || isChristmas;
  }

  isWeekendOrHoliday() {
    return this.isWeekend() || this.isHoliday();
  }

  isFriday() {
    return this.date.getDay() === 5;
  }

  isMonday() {
    return this.date.getDay() === 1;
  }

  isStartOfMonth() {
    return this.getDay() === 1;
  }

  isEndOfMonth() {
    return this.getDay() === util.getDaysInMonth(this.getYear(), this.getMonth());
  }

  isStartOfQuarter() {
    return (
      this.getDay() === 1 &&
      (this.getMonth() === 1 || this.getMonth() === 4 || this.getMonth() === 7 || this.getMonth() === 10)
    );
  }

  isEndOfQuarter() {
    return (
      this.getDay() === util.getDaysInMonth(this.getYear(), this.getMonth()) &&
      (this.getMonth() === 3 || this.getMonth() === 6 || this.getMonth() === 9 || this.getMonth() === 12)
    );
  }

  isStartOfYear() {
    return this.getDay() === 1 && this.getMonth() === 1;
  }

  isEndOfYear() {
    return this.getDay() === 31 && this.getMonth() === 12;
  }

  add(n, timeunit) {
    let date = new Date();

    if (timeunit.toLowerCase().indexOf('year') === 0) {
      date = util.addYears(this.getDate(), n);
    } else if (timeunit.toLowerCase().indexOf('month') === 0) {
      date = util.addMonths(this.getDate(), n);
    } else if (timeunit.toLowerCase().indexOf('day') === 0) {
      date = util.addDays(this.getDate(), n);
    } else if (timeunit.toLowerCase().indexOf('millis') === 0) {
      const millis = this.getDate().getTime() + n;
      date = new Date(millis);
    }

    return new TimeStamp(date.getTime());
  }

  subtract(n, timeunit) {
    let date = new Date();

    if (timeunit.toLowerCase().indexOf('year') === 0) {
      date = util.subtractYears(this.getDate(), n);
    } else if (timeunit.toLowerCase().indexOf('month') === 0) {
      date = util.subtractMonths(this.getDate(), n);
    } else if (timeunit.toLowerCase().indexOf('day') === 0) {
      date = util.subtractDays(this.getDate(), n);
    } else if (timeunit.toLowerCase().indexOf('millis') === 0) {
      const millis = this.getDate().getTime() - n;
      date = new Date(millis);
    }

    return new TimeStamp(date.getTime());
  }

  clone() {
    return new TimeStamp(this.getMillis());
  }
}

class Path {
  constructor(ctx) {
    this.ctx = ctx
    this.coords = [];
  }

  moveTo(x, y) {
    this.coords.push({
      x,
      y,
      join: false
    });

    return this;
  }

  lineTo(x, y) {
    this.coords.push({
      x,
      y,
      join: true
    });

    return this;
  }

  clear() {
    this.coords = [];

    return this;
  }

  stroke(color) {
    this.ctx.save();
    this.ctx.beginPath();

    for (const pt of this.coords) {
      const x = Math.round(pt.x) + 0.5;
      const y = Math.round(pt.y) + 0.5;

      if (pt.join) {
        this.ctx.lineTo(x, y);
      } else {
        this.ctx.moveTo(x, y);
      }
    }

    this.ctx.closePath();

    this.ctx.strokeStyle = color;

    this.ctx.stroke();
    this.ctx.restore();

    return this;
  }
}

class Rectangle {
  constructor(x, y, w, h) {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
    this.left = this.x;
    this.right = this.x + this.w - 1;
    this.top = this.y;
    this.bottom = this.y + this.h - 1;
  }

  paint(ctx, fillColor, borderColor) {
    ctx.save();

    const x = Math.round(this.x);
    const y = Math.round(this.y);
    const w = Math.round(this.right) - Math.round(this.left) + 1;
    const h = Math.round(this.h);

    if (fillColor) {
      ctx.fillStyle = fillColor;

      ctx.fillRect(x, y, w, h);
    }

    if (borderColor) {
      ctx.strokeStyle = borderColor;

      ctx.strokeRect(
        x + .5,
        y + .5,
        w - 1,
        h - 1
      );
    }

    ctx.restore();
  }

  clear(ctx) {
    ctx.clearRect(this.x, this.y, this.w, this.h);
  }

  contains(point) {
    return (
      point.x >= this.left &&
      point.x <= this.right &&
      point.y >= this.top &&
      point.y <= this.bottom
    );
  }
}

class Shape {
  constructor() {
    this.coords = [];
  }

  lineTo(x, y) {
    this.coords.push({
      x: x + .5,
      y: y + .5
    });

    return this;
  }

  clear() {
    this.coords = [];

    return this;
  }

  paint(ctx, fillColor, borderColor) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(this.coords[0].x, this.coords[0].y);

    for (let i = 1; i < this.coords.length; i++) {
      ctx.lineTo(this.coords[i].x, this.coords[i].y);
    }

    ctx.closePath();

    if (fillColor) {
      ctx.fillStyle = fillColor;

      ctx.fill();
    }

    if (borderColor) {
      ctx.strokeStyle = borderColor;

      ctx.stroke();
    }

    ctx.restore();
  }
}

class ContextMenu {
  constructor(args = {}) {
    this.opts = {
      x: 0,
      y: 0,
      colorFill: '#f0f0f0',
      colorBorder: 'black',
      anchor: 'top',
      callee: null,
      menuItems: {},
      ...args
    };

    this.menu = document.createElement('div');

    this.menu.classList.add('ranger');
    this.menu.classList.add('contextmenu');

    this.menu.style.position = 'absolute';
    this.menu.style.top = `${this.opts.y}px`;
    this.menu.style.left = `${this.opts.x}px`;
    this.menu.style.backgroundColor = this.opts.colorFill;
    this.menu.style.border = `1px solid ${this.opts.colorBorder}`;
    this.menu.style.boxShadow = '0px 0px 5px #666';
    this.menu.style.zIndex = 999;
    this.menu.style.fontSize = '14px';

    const closeButton = document.createElement('div');

    closeButton.classList.add('close');

    closeButton.innerHTML = '&times;';
    closeButton.style.textAlign = 'right';
    closeButton.style.padding = '0px 2px';
    closeButton.style.float = 'right';
    closeButton.style.fontSize = '21px';
    closeButton.style.lineHeight = '1';
    closeButton.style.color = 'black';
    closeButton.style.textShadow = '0px 1px 0px white';
    closeButton.style.cursor = 'default';
    closeButton.style.fontWeight = 'normal';
    closeButton.style.opacity = '0.2';

    closeButton.addEventListener('mouseover', () => {
      closeButton.style.cursor = 'pointer';
      closeButton.style.fontWeight = 'bold';
      closeButton.style.opacity = '0.5';
    });

    closeButton.addEventListener('mouseout', () => {
      closeButton.style.cursor = 'default';
      closeButton.style.fontWeight = 'normal';
      closeButton.style.opacity = '0.2';
    });

    closeButton.addEventListener('click', () => {
      this.destroy();
    });

    this.menu.appendChild(closeButton);

    for (const item in this.opts.menuItems) {
      const menuItem = document.createElement('div');

      menuItem.classList.add('menuitem');

      menuItem.innerText = item;
      menuItem.style.padding = '2px 4px';

      menuItem.addEventListener('mouseover', () => {
        menuItem.style.cursor = 'pointer';
        menuItem.style.background = '#c8c8c8';
      });

      menuItem.addEventListener('mouseout', () => {
        menuItem.style.cursor = 'default';
        menuItem.style.background = 'none';
      });

      menuItem.addEventListener('click', () => {
        const fn = this.opts.menuItems[menuItem.innerText];

        fn.call(this.opts.callee);
        this.destroy();
      });

      this.menu.appendChild(menuItem);
    }
  }

  show() {
    if (this.isShown()) {
      return;
    }

    document.body.appendChild(this.menu);

    const menuBoundingClientRect = this.menu.getBoundingClientRect();

    this.menu.style.left = `${menuBoundingClientRect.left - menuBoundingClientRect.width / 2}px`;

    if (this.opts.anchor !== 'bottom') {
      return;
    }

    // shift upwards if we are bottom aligned
    this.menu.style.top = `${this.opts.y - menuBoundingClientRect.height - 1}px`;
  }

  destroy() {
    document.body.removeChild(this.menu);
  }

  isShown() {
    return document.body.querySelectorAll('.ranger.contextmenu').length > 0;
  }
}

class LeftArrow {
  constructor(parent, args = {}) {
    this.opts = {
      height: 15,
      colorFill: '#f0f0f0',
      colorBorder: 'black',
      colorHover: '#c8c8c8',
      ...args
    };
    this.parent = parent;
    this.canvas = parent.canvas;
    this.ctx = parent.ctx;
    this.isHovered = false;
    this.timerId = 0;
    this.pauseTime = 500;
    this.bounds = new Rectangle(
      0,
      this.parent.opts.height / 2 - this.opts.height / 2,
      this.opts.height,
      this.opts.height
    );
    this.arrow = new Shape()
      .lineTo(this.bounds.right - 1, this.bounds.top)
      .lineTo(this.bounds.left, this.bounds.top + this.bounds.h / 2)
      .lineTo(this.bounds.right - 1, this.bounds.bottom);

    pubsub.on('mousemove', this.onMouseMove, this);
    pubsub.on('mouseout', this.onMouseOut, this);
    pubsub.on('mousedown', this.onMouseDown, this);
    pubsub.on('mouseup', this.onMouseUp, this);
  }

  paint() {
    this.bounds.clear(this.ctx);

    if (this.isHovered) {
      this.arrow.paint(this.ctx, this.opts.colorHover, this.opts.colorBorder);
    } else {
      this.arrow.paint(this.ctx, this.opts.colorFill, this.opts.colorBorder);
    }
  }

  onMouseMove(oldMousePosition, newMousePosition) {
    this.setHovered(this.bounds.contains(newMousePosition));
  }

  onMouseOut() {
    this.setHovered(false);
  }

  onMouseDown(mousePosition) {
    if (!this.bounds.contains(mousePosition)) {
      return;
    }

    this.nudge();

    this.timerId = window.setInterval(() => {
      this.nudge();
    }, this.pauseTime);
  }

  onMouseUp() {
    window.clearInterval(this.timerId);
  }

  nudge() {
    pubsub.emit('nudge left');
  }

  setHovered(isHovered) {
    const isHoveredPrev = this.isHovered;
    this.isHovered = isHovered;

    if (isHoveredPrev !== this.isHovered) {
      this.paint();
    }
  }
}

class RightArrow {
  constructor(parent, args = {}) {
    this.opts = {
      height: 15,
      colorFill: '#f0f0f0',
      colorBorder: 'black',
      colorHover: '#c8c8c8',
      ...args
    };
    this.parent = parent;
    this.canvas = parent.canvas;
    this.ctx = parent.ctx;
    this.isHovered = false;
    this.timerId = 0;
    this.pauseTime = 500;
    this.bounds = new Rectangle(
      this.parent.opts.width - this.opts.height,
      this.parent.opts.height / 2 - this.opts.height / 2,
      this.opts.height,
      this.opts.height
    );
    this.arrow = new Shape()
      .lineTo(this.bounds.left + 1, this.bounds.top)
      .lineTo(this.bounds.right, this.bounds.top + this.bounds.h / 2)
      .lineTo(this.bounds.left + 1, this.bounds.bottom);

    pubsub.on('mousemove', this.onMouseMove, this);
    pubsub.on('mouseout', this.onMouseOut, this);
    pubsub.on('mousedown', this.onMouseDown, this);
    pubsub.on('mouseup', this.onMouseUp, this);
  }

  paint() {
    this.bounds.clear(this.ctx);

    if (this.isHovered) {
      this.arrow.paint(this.ctx, this.opts.colorHover, this.opts.colorBorder);
    } else {
      this.arrow.paint(this.ctx, this.opts.colorFill, this.opts.colorBorder);
    }
  }

  onMouseMove(oldMousePosition, newMousePosition) {
    this.setHovered(this.bounds.contains(newMousePosition));
  }

  onMouseOut() {
    this.setHovered(false);
  }

  onMouseDown(mousePosition) {
    if (!this.bounds.contains(mousePosition)) {
      return;
    }

    this.nudge();

    this.timerId = window.setInterval(() => {
      this.nudge();
    }, this.pauseTime);
  }

  onMouseUp() {
    window.clearInterval(this.timerId);
  }

  nudge() {
    pubsub.emit('nudge right');
  }

  setHovered(isHovered) {
    const isHoveredPrev = this.isHovered;
    this.isHovered = isHovered;

    if (isHoveredPrev !== this.isHovered) {
      this.paint();
    }
  }
}

class Track {
  constructor(parent, args = {}) {
    this.opts = {
      height: 15,
      minTime: new TimeStamp('1 year ago'),
      maxTime: new TimeStamp(MAX_RANGE_SLIDER_DATE),
      colorFill: '#f0f0f0',
      colorBorder: 'black',
      ...args
    };
    this.parent = parent;
    this.canvas  = parent.canvas;
    this.ctx     = parent.ctx;
    this.bounds = new Rectangle(
      this.parent.leftArrow.opts.height,
      this.parent.opts.height / 2 - this.opts.height / 2,
      this.parent.opts.width - this.parent.leftArrow.opts.height - this.parent.rightArrow.opts.height,
      this.opts.height
    );
  }

  paint() {
    // fill the bounding rectangle
    this.bounds.paint(this.ctx, this.opts.colorFill, this.opts.colorBorder);

    // render the year labels
    this.paintLabels();
  }

  paintLabels() {
    const nYears = (this.opts.maxTime.getMillis() - this.opts.minTime.getMillis()) / (1000 * 60 * 60 * 24 * 365);
    const pxPerYear = this.bounds.w / nYears;

    // working from right to left, step month to month, drawing a label if appropriate
    let timestamp = pxPerYear < 1700 ? new TimeStamp(this.opts.maxTime.getMonth(), 1, this.opts.maxTime.getYear()) : this.opts.maxTime.clone();

    while (timestamp.ge(this.opts.minTime)) {
      if (pxPerYear < 3) {
        // every 10 yrs
        if (timestamp.getYear() % 10 === 0 && timestamp.getMonth() === 1 && timestamp.getDay() === 1) {
          this.paintLabel(timestamp, 'YY');
        }

        timestamp = timestamp.subtract(1, 'month');
        continue;
      }

      if (pxPerYear >= 3 && pxPerYear < 15) {
        // every 5 years
        if (timestamp.getYear() % 5 === 0 && timestamp.getMonth() === 1 && timestamp.getDay() === 1) {
          this.paintLabel(timestamp, 'YY');
        }

        timestamp = timestamp.subtract(1, 'month');
        continue;
      }

      if (pxPerYear >= 15 && pxPerYear < 25) {
        // every year
        if (timestamp.getMonth() === 1 && timestamp.getDay() === 1) {
          this.paintLabel(timestamp, 'YY');
        }

        timestamp = timestamp.subtract(1, 'month');
        continue;
      }

      if (pxPerYear >= 25 && pxPerYear < 170) {
        // every year
        if (timestamp.getMonth() === 1 && timestamp.getDay() === 1) {
          this.paintLabel(timestamp, 'YYYY');
        }

        timestamp = timestamp.subtract(1, 'month');
        continue;
      }

      if (pxPerYear >= 170 && pxPerYear < 270) {
        // every 3 months
        if ((timestamp.getMonth()-1) % 3 === 0 && timestamp.getDay() === 1) {
          this.paintLabel(timestamp, 'MMM');
        }

        timestamp = timestamp.subtract(1, 'month');
        continue;
      }

      if (pxPerYear >= 270 && pxPerYear < 1700) {
        // every month
        if (timestamp.getDay() === 1) {
          this.paintLabel(timestamp, 'MMM');
        }

        timestamp = timestamp.subtract(1, 'month');
        continue;
      }

      if (pxPerYear >= 1700 && pxPerYear < 7100) {
        // every 5th day
        if (timestamp.getDay() === 1 || timestamp.getDay() % 5 === 0) {
          this.paintLabel(timestamp, 'D');
        }

        timestamp = timestamp.subtract(1, 'day');
        continue;
      }

      if (pxPerYear >= 7100) {
        // every day
        this.paintLabel(timestamp, 'D');
        timestamp = timestamp.subtract(1, 'day');
      }
    }
  }

  paintLabel(timestamp, format) {
    this.ctx.fillStyle = this.opts.colorBorder;
    const fontSize = 10;
    this.ctx.font = fontSize + 'px arial';
    const px = this.parent.timestampToPixel(timestamp);

    new Path(this.ctx)
      .moveTo(px, this.bounds.top)
      .lineTo(px, this.bounds.bottom)
      .stroke(this.opts.borderColor);

    if (format === 'MMM' && timestamp.getMonth() === 1) {
      format = 'YYYY';
    } else if ((format === 'D' || format === 'DD') && timestamp.getDay() === 1) {
      format = 'MMM';
    }

    const label = timestamp.format(format);

    this.ctx.fillText(label, px + 1, this.bounds.y + this.bounds.h / 2 + fontSize / 2 - 2);
  }

  getRange() {
    const startMillis = this.parent.pixelToTimestamp(this.bounds.left).getMillis();
    const endMillis   = this.parent.pixelToTimestamp(this.bounds.right).getMillis();

    return endMillis - startMillis;
  }
}

class Slider {
  constructor(parent, args = {}) {
    this.opts = {
      minTime: new TimeStamp('1 year ago'),
      maxTime: new TimeStamp(MAX_RANGE_SLIDER_DATE),
      colorFill: '2000(240,240,240,.8)',
      colorBorder: 'black',
      colorHover: 'rgba(200,200,200,.8)',
      widthGrab: 10,
      ...args
    };
    this.parent = parent;
    this.canvas = parent.canvas;
    this.ctx = parent.ctx;
    this.isDragging = false;
    this.isExpandingRight = false;
    this.isExpandingLeft = false;
    this.isHoveredCenter = false;
    this.isHoveredRight = false;
    this.isHoveredLeft = false;
    this.timerSliderHalt = 0;
    this.millisSliderHalt = 250;
    this.pauseTimeTrackExpansion = 50;
    this.timerIdTrackExpansion = 0;
    this.widthMin = 2 * this.opts.widthGrab;
    const pxLeft = this.parent.timestampToPixel(this.opts.minTime);
    const pxRight = this.parent.timestampToPixel(this.opts.maxTime);
    this.bounds = new Rectangle(pxLeft, 0, pxRight - pxLeft + 1, this.parent.opts.height);
    this.leftGrab = new Rectangle(pxLeft, 0, this.opts.widthGrab, this.parent.opts.height);
    this.rightGrab = new Rectangle(pxRight - this.opts.widthGrab + 1, 0, this.opts.widthGrab, this.parent.opts.height);

    pubsub.on('mousemove', this.onMouseMove, this);
    pubsub.on('mousedown', this.onMouseDown, this);
    pubsub.on('mouseup', this.onMouseUp, this);
    pubsub.on('mouseover', this.onMouseOver, this);
    pubsub.on('mouseout', this.onMouseOut, this);
    pubsub.on('change inputs', this.onChangeInputs, this);
    pubsub.on('contextmenu', this.onContextMenu, this);
    pubsub.on('nudge left', this.onNudgeLeft, this);
    pubsub.on('nudge right', this.onNudgeRight, this);
  }

  paint() {
    // background
    const colorFill = (this.isHovered() || this.isDraggingOrExpanding()) ? this.opts.colorHover : this.opts.colorFill;
    const colorBorder = this.opts.colorBorder;

    this.bounds.paint(this.ctx, colorFill, colorBorder);

    // grab handles
    if (this.isHoveredRight || this.isExpandingRight) {
      this.paintRightGrabHandle();
    } else if (this.isHoveredLeft || this.isExpandingLeft) {
      this.paintLeftGrabHandle();
    }
  }

  paintLeftGrabHandle() {
    const p = new Path(this.ctx);

    for (let i = 1; i <= 7; i += 2) {
      p.moveTo(this.bounds.x + i, this.bounds.y + 2).lineTo(this.bounds.x + i, this.bounds.h - 2);
    }

    p.stroke('white');
    p.clear();

    for (let i = 2; i <= 8; i += 2) {
      p.moveTo(this.bounds.x + i, this.bounds.y + 2).lineTo(this.bounds.x + i, this.bounds.h - 2);
    }

    p.stroke('#333333');
  }

  paintRightGrabHandle() {
    const p = new Path(this.ctx);

    for (let i = 3; i <= 9; i += 2) {
      p.moveTo(this.bounds.x + this.bounds.w - i, this.bounds.y + 2).lineTo(this.bounds.x + this.bounds.w - i, this.bounds.h - 2);
    }

    p.stroke('white');
    p.clear();

    for (let i = 2; i <= 8; i += 2) {
      p.moveTo(this.bounds.x + this.bounds.w - i, this.bounds.y + 2).lineTo(this.bounds.x + this.bounds.w - i, this.bounds.h - 2);
    }

    p.stroke('#333333');
  }

  onContextMenu(mousePosition) {
    if (!this.bounds.contains(mousePosition)) {
      return;
    }

    const globalMousePosition = this.parent.localToGlobal({
      x: mousePosition.x,
      y: 0
    });

    const menu = new ContextMenu({
      x: globalMousePosition.x,
      y: globalMousePosition.y,
      colorBorder: this.parent.opts.menu.border,
      colorFill: this.parent.opts.menu.fill,
      anchor: 'bottom',
      callee: this,
      menuItems: {
        '3 Years': function () {
          pubsub.emit('change inputs', this.opts.maxTime.subtract(3, 'years'), this.opts.maxTime);
        },
        '2 Years': function () {
          pubsub.emit('change inputs', this.opts.maxTime.subtract(2, 'years'), this.opts.maxTime);
        },
        '1 Year': function () {
          pubsub.emit('change inputs', this.opts.maxTime.subtract(1, 'years'), this.opts.maxTime);
        },
        '6 Months': function () {
          pubsub.emit('change inputs', this.opts.maxTime.subtract(6, 'months'), this.opts.maxTime);
        },
        '3 Months': function () {
          pubsub.emit('change inputs', this.opts.maxTime.subtract(3, 'months'), this.opts.maxTime);
        },
        '1 Month': function () {
          pubsub.emit('change inputs', this.opts.maxTime.subtract(1, 'month'), this.opts.maxTime);
        },
        'YTD': function () {
          const maxTime = new TimeStamp(MAX_RANGE_SLIDER_DATE);
          const minTime = new TimeStamp(1, 1, maxTime.getYear());

          pubsub.emit('change inputs', minTime, maxTime);
        }
      }
    });

    menu.show();
  }

  onMouseDown(mousePosition) {
    if (this.leftGrab.contains(mousePosition)) {
      this.startExpandLeft();
    } else if (this.rightGrab.contains(mousePosition)) {
      this.startExpandRight();
    } else if (this.bounds.contains(mousePosition)) {
      this.startDrag();
    }
  }

  onMouseUp() {
    if (!this.isDraggingOrExpanding()) {
      return;
    }

    this.stopMeasuringSliderPause();
    this.stopTrackExpansion();
    this.stopDrag();
  }

  onMouseOver() {
  }

  onMouseOut() {
    this.checkHoverStatus();
  }

  onMouseMove(oldMousePosition, newMousePosition) {
    // the mouse is moving on the widget or possibly on the document in general
    // figure out how much to drag
    const deltaX = newMousePosition.x - oldMousePosition.x;

    if (deltaX === 0) {
      return;
    }

    // check to see if the slider is hovered and where
    this.checkHoverStatus(newMousePosition);

    // are we currently moving the slider?
    if (!this.isDraggingOrExpanding()) {
      return;
    }

    // we are dragging, so we can stop measuring the slider pause time
    this.stopMeasuringSliderPause();

    if (this.isExpandingLeft) {
      const isSliderTooSmall = (deltaX > 0 && this.bounds.w - deltaX < this.widthMin);
      const isBeyondLeftEdge = (deltaX < 0 && this.bounds.left === this.parent.track.bounds.left);

      if (!isSliderTooSmall && !isBeyondLeftEdge) {
        this.setPixelBounds(this.bounds.left + deltaX, this.bounds.right);
      }

      // begin auto expansion of track if the slider is pressed against the left edge
      if (this.bounds.left === this.parent.track.bounds.left) {
        this.startTrackExpansion();
      } else {
        // set a timer to measure how long we pause dragging
        this.startMeasuringSliderPause();
      }

      return;
    }

    if (this.isExpandingRight) {
      const isSliderTooSmall  = (deltaX < 0 && this.bounds.w + deltaX < this.widthMin);
      const isBeyondRightEdge = (deltaX > 0 && this.bounds.right === this.parent.track.bounds.right);

      if (!isSliderTooSmall && !isBeyondRightEdge) {
        this.setPixelBounds(this.bounds.left, this.bounds.right + deltaX);
        // set a timer to measure how long we pause dragging
        this.startMeasuringSliderPause();
      }

      return;
    }

    const isBeyondLeftEdge  = (deltaX < 0 && this.bounds.left === this.parent.track.bounds.left);
    const isBeyondRightEdge = (deltaX > 0 && this.bounds.right === this.parent.track.bounds.right);

    if (!isBeyondLeftEdge && !isBeyondRightEdge) {
      this.setPixelBounds(this.bounds.left + deltaX, this.bounds.right + deltaX);
    }

    // begin auto expansion of track if the slider is pressed against the left edge
    if (this.bounds.left === this.parent.track.bounds.left) {
      this.startTrackExpansion();
    } else {
      // set a timer to measure how long we pause dragging
      this.startMeasuringSliderPause();
    }
  }

  startTrackExpansion() {
    if (
      this.timerIdTrackExpansion !== 0 ||
      !this.parent.track.opts.minTime.gt(this.parent.minAllowableTime)
    ) {
      return;
    }

    this.stopMeasuringSliderPause();

    this.timerIdTrackExpansion = window.setInterval(() => {
      if (!this.parent.track.opts.minTime.gt(this.parent.minAllowableTime)) {
        this.stopTrackExpansion();
        this.parent.setDates(this.opts.minTime, this.opts.maxTime);
        return;
      }

      let timestamp = this.parent.track.opts.minTime.subtract(0.01 * this.parent.track.getRange(), 'millis');

      if (timestamp.lt(this.parent.minAllowableTime)) {
        timestamp = this.parent.minAllowableTime;
      }

      this.parent.track.opts.minTime = timestamp;
      this.opts.minTime = timestamp;

      this.parent.paint();
    }, this.pauseTimeTrackExpansion);
  }

  stopTrackExpansion() {
    if (this.timerIdTrackExpansion === 0) {
      return;
    }

    window.clearTimeout(this.timerIdTrackExpansion);

    this.timerIdTrackExpansion = 0;

    // expand the track once more to move the slider back from the left edge
    let timestamp = this.parent.track.opts.minTime.subtract(this.parent.track.getRange(), 'millis');

    if (timestamp.lt(this.parent.minAllowableTime)) {
      timestamp = this.parent.minAllowableTime;
    }

    this.parent.track.opts.minTime = timestamp;
    const pxLeft = this.parent.timestampToPixel(this.opts.minTime);
    const pxRight = this.parent.timestampToPixel(this.opts.maxTime);

    this.setPixelBounds(pxLeft, pxRight);
  }

  onNudgeLeft() {
    const chartStore = useChartStore();

    this.startDrag();

    let minTimeNew = this.opts.minTime.clone();
    let maxTimeNew = this.opts.maxTime.clone();

    if (chartStore.period === PERIOD_OPTION_WEEKLY.value) {
      // find prev FRI
      maxTimeNew = maxTimeNew.subtract(1, 'day');

      while (!maxTimeNew.isFriday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find prev MON
      minTimeNew = minTimeNew.subtract(1, 'day');

      while (!minTimeNew.isMonday()) {
        minTimeNew = minTimeNew.subtract(1, 'day');
      }
    } else if (chartStore.period === PERIOD_OPTION_MONTHLY.value) {
      // find prev end-of-month
      maxTimeNew = maxTimeNew.subtract(1, 'day');

      while (!maxTimeNew.isEndOfMonth()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find prev start-of-month
      const currentMonth = minTimeNew.getMonth();
      minTimeNew = minTimeNew.subtract(1, 'day');

      while (!(minTimeNew.isStartOfMonth() && minTimeNew.getMonth() !== currentMonth)) {
        minTimeNew = minTimeNew.subtract(1, 'day');
      }

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else if (chartStore.period === PERIOD_OPTION_QUARTERLY.value) {
      // find prev end-of-quarter
      maxTimeNew = maxTimeNew.subtract(1, 'day');

      while (!maxTimeNew.isEndOfQuarter()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find prev start-of-quarter
      const currentMonth = minTimeNew.getMonth();
      minTimeNew = minTimeNew.subtract(1, 'day');

      while (!(minTimeNew.isStartOfQuarter() && minTimeNew.getMonth() !== currentMonth)) {
        minTimeNew = minTimeNew.subtract(1, 'day');
      }

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else if (chartStore.period === PERIOD_OPTION_YEARLY.value) {
      // find prev end-of-year
      maxTimeNew = maxTimeNew.subtract(1, 'day');

      while (!maxTimeNew.isEndOfYear()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find prev start-of-year
      const currentYear = minTimeNew.getYear();
      minTimeNew = minTimeNew.subtract(1, 'day');

      while (!(minTimeNew.isStartOfYear() && minTimeNew.getYear() !== currentYear)) {
        minTimeNew = minTimeNew.subtract(1, 'day');
      }

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else { // daily and intraday
      maxTimeNew = maxTimeNew.subtract(1, 'day');

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      minTimeNew = minTimeNew.subtract(1, 'day');

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.subtract(1, 'day');
      }
    }

    // make sure we are within allowable times
    if (minTimeNew.lt(this.parent.minAllowableTime)) {
      this.setTimeBounds(this.parent.minAllowableTime, this.opts.maxTime);
    } else if (maxTimeNew.gt(this.parent.maxAllowableTime)) {
      this.setTimeBounds(this.opts.minTime, this.parent.maxAllowableTime);
    } else {
      this.setTimeBounds(minTimeNew, maxTimeNew);
    }

    this.stopDrag();
  }

  onNudgeRight() {
    const chartStore = useChartStore();

    this.startDrag();

    let minTimeNew = this.opts.minTime.clone();
    let maxTimeNew = this.opts.maxTime.clone();

    if (chartStore.period === PERIOD_OPTION_WEEKLY.value) {
      // find next FRI
      maxTimeNew = maxTimeNew.add(1, 'day');

      while (!maxTimeNew.isFriday()) {
        maxTimeNew = maxTimeNew.add(1, 'day');
      }

      // find next MON
      minTimeNew = minTimeNew.add(1, 'day');

      while (!minTimeNew.isMonday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else if (chartStore.period === PERIOD_OPTION_MONTHLY.value) {
      // find next end-of-month
      const currentMonth = maxTimeNew.getMonth();
      maxTimeNew = maxTimeNew.add(1, 'day');

      while (!(maxTimeNew.isEndOfMonth() && maxTimeNew.getMonth() !== currentMonth)) {
        maxTimeNew = maxTimeNew.add(1, 'day');
      }

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find next start-of-month
      minTimeNew = minTimeNew.add(1, 'day');

      while (!minTimeNew.isStartOfMonth()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else if (chartStore.period === PERIOD_OPTION_QUARTERLY.value) {
      // find next end-of-quarter
      const currentMonth = maxTimeNew.getMonth();
      maxTimeNew = maxTimeNew.add(1, 'day');

      while (!(maxTimeNew.isEndOfQuarter() && maxTimeNew.getMonth() !== currentMonth)) {
        maxTimeNew = maxTimeNew.add(1, 'day');
      }

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find next start-of-quarter
      minTimeNew = minTimeNew.add(1, 'day');

      while (!minTimeNew.isStartOfQuarter()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else if (chartStore.period === PERIOD_OPTION_YEARLY.value) {
      // find next end-of-year
      const currentYear = maxTimeNew.getYear();
      maxTimeNew = maxTimeNew.add(1, 'day');

      while (!(maxTimeNew.isEndOfYear() && maxTimeNew.getYear() !== currentYear)) {
        maxTimeNew = maxTimeNew.add(1, 'day');
      }

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.subtract(1, 'day');
      }

      // find next start-of-year
      minTimeNew = minTimeNew.add(1, 'day');

      while (!minTimeNew.isStartOfYear()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    } else { // daily and intraday
      maxTimeNew = maxTimeNew.add(1, 'day');

      while (maxTimeNew.isWeekendOrHoliday()) {
        maxTimeNew = maxTimeNew.add(1, 'day');
      }

      minTimeNew = minTimeNew.add(1, 'day');

      while (minTimeNew.isWeekendOrHoliday()) {
        minTimeNew = minTimeNew.add(1, 'day');
      }
    }

    // make sure we are within allowable times
    if (minTimeNew.lt(this.parent.minAllowableTime)) {
      this.setTimeBounds(this.parent.minAllowableTime, this.opts.maxTime);
    } else if (maxTimeNew.gt(this.parent.maxAllowableTime)) {
      this.setTimeBounds(this.opts.minTime, this.parent.maxAllowableTime);
    } else {
      this.setTimeBounds(minTimeNew, maxTimeNew);
    }

    this.stopDrag();
  }

  onChangeInputs(startTime, endTime) {
    this.startDrag();
    this.setTimeBounds(startTime, endTime);
    this.stopDrag();
  }

  startMeasuringSliderPause() {
    // when the dragging is paused for a certain amount of time, call setDates
    this.timerSliderHalt = window.setTimeout(() => {
      this.parent.setDates(this.opts.minTime, this.opts.maxTime);
    }, this.millisSliderHalt);
  }

  stopMeasuringSliderPause() {
    window.clearTimeout(this.timerSliderHalt);
  }

  isDraggingOrExpanding() {
    return this.isDragging || this.isExpandingLeft || this.isExpandingRight;
  }

  startDrag() {
    this.isDragging = true;
    this.isExpandingLeft = false;
    this.isExpandingRight = false;
  }

  startExpandLeft() {
    this.isDragging = false;
    this.isExpandingLeft = true;
    this.isExpandingRight = false;
  }

  startExpandRight() {
    this.isDragging = false;
    this.isExpandingLeft = false;
    this.isExpandingRight = true;
  }

  stopDrag() {
    this.isDragging = false;
    this.isExpandingLeft = false;
    this.isExpandingRight = false;

    this.parent.paint();
    this.parent.setDates(this.opts.minTime, this.opts.maxTime);
  }

  isHovered() {
    return this.isHoveredLeft || this.isHoveredRight || this.isHoveredCenter;
  }

  checkHoverStatus(mousePosition) {
    const isHoveredCenterPrev = this.isHoveredCenter;
    const isHoveredLeftPrev = this.isHoveredLeft;
    const isHoveredRightPrev = this.isHoveredRight;

    if (!mousePosition || !this.bounds.contains(mousePosition)) {
      this.isHoveredCenter = false;
      this.isHoveredLeft = false;
      this.isHoveredRight = false;
    } else if (this.leftGrab.contains(mousePosition)) {
      this.isHoveredCenter = false;
      this.isHoveredLeft = true;
      this.isHoveredRight = false;
    } else if (this.rightGrab.contains(mousePosition)) {
      this.isHoveredCenter = false;
      this.isHoveredLeft = false;
      this.isHoveredRight = true;
    } else {
      this.isHoveredCenter = true;
      this.isHoveredLeft = false;
      this.isHoveredRight = false;
    }

    // repaint the slider when we start/stop hover
    const isDirty = (
      isHoveredCenterPrev !== this.isHoveredCenter ||
      isHoveredLeftPrev !== this.isHoveredLeft ||
      isHoveredRightPrev !== this.isHoveredRight
    );

    if (isDirty) {
      this.parent.paint();
    }
  }

  setTimeBounds(timeLeft, timeRight) {
    // this is called every time we change the start/end input fields or select something from the context menu
    // try to make the slider 25% of the track width
    const maxTimeSlider = timeRight;
    const minTimeSlider = timeLeft;
    const millis = maxTimeSlider.getMillis() - minTimeSlider.getMillis();
    let maxTimeTrack = new TimeStamp(MAX_RANGE_SLIDER_DATE);
    let minTimeTrack = maxTimeTrack.subtract(4 * millis, 'millis');

    if (minTimeTrack.lt(this.parent.minAllowableTime)) {
      minTimeTrack = this.parent.minAllowableTime;
    }

    if (minTimeSlider.lt(minTimeTrack)) {
      minTimeTrack = minTimeSlider;
      maxTimeTrack = minTimeTrack.add(4 * millis, 'millis');

      if (maxTimeTrack.gt(this.parent.maxAllowableTime)) {
        maxTimeTrack = this.parent.maxAllowableTime;
      }
    }

    this.parent.track.opts.minTime = minTimeTrack;
    this.parent.track.opts.maxTime = maxTimeTrack;
    const pxLeft = this.parent.timestampToPixel(minTimeSlider);
    const pxRight = this.parent.timestampToPixel(maxTimeSlider);

    this.setPixelBounds(pxLeft, pxRight);
  }

  setPixelBounds(pxLeft, pxRight) {
    // this is called every time we move the slider
    // make intelligent adjustments if the slider falls outside of the track
    if (!pxLeft && !pxRight) {
      // we haven't been supplied a new left/right pixel for the slider, just use the
      // existing pixel locations (this implies that the track has changed but the slider hasn't)
      pxLeft = this.bounds.left;
      pxRight = this.bounds.right;
    }

    const w = pxRight - pxLeft + 1;
    const tLeft = this.parent.pixelToTimestamp(pxLeft);
    const tRight = this.parent.pixelToTimestamp(pxRight);

    if (
      pxLeft === this.bounds.left &&
      pxRight === this.bounds.right
    ) {
      // slider hasn't moved, do nothing
    } else if (pxLeft > pxRight) {
      // invalid pixels, do nothing
    } else if (pxRight > this.parent.track.bounds.right) {
      // the slider is off the rightmost edge, we need to add days to right edge of the track (if we can)
      // keep the slider against the right edge, hold the slider width constant
      if (tRight.le(this.parent.maxAllowableTime)) {
        this.parent.track.opts.maxTime = tRight;
      } else {
        const deltaMillis = tRight.getMillis() - this.parent.track.opts.maxTime.getMillis();
        this.parent.track.opts.maxTime = this.parent.maxAllowableTime;
        this.parent.track.opts.minTime = this.parent.track.opts.minTime.add(deltaMillis, 'millis');
      }

      pxRight = this.parent.track.bounds.right;
      pxLeft = pxRight - w + 1;
    } else if (pxLeft < this.parent.track.bounds.left) {
      // the slider is off the leftmost edge, we need to add days to the left edge of the track (if we can)
      // keep the slider against the left edge, hold the slider width constant
      if (tLeft.ge(this.parent.minAllowableTime)) {
        this.parent.track.opts.minTime = tLeft;
      } else {
        const deltaMillis = this.parent.track.opts.minTime.getMillis() - tLeft.getMillis();
        this.parent.track.opts.minTime = this.parent.minAllowableTime;
        this.parent.track.opts.maxTime = this.parent.track.opts.maxTime.subtract(deltaMillis, 'millis');
      }

      pxLeft = this.parent.track.bounds.left;

      if (this.bounds.right < this.parent.track.bounds.right) {
        pxRight = pxLeft + w - 1;
      }
    }

    this.setBounds(pxLeft, pxRight);
  }

  setBounds(pxLeft, pxRight) {
    const x = pxLeft;
    const w = pxRight - pxLeft + 1;
    const y = this.bounds.y;
    const h = this.bounds.h;
    this.bounds = new Rectangle(x, y, w, h);
    this.leftGrab = new Rectangle(this.bounds.left, 0, this.opts.widthGrab, this.parent.opts.height);
    this.rightGrab = new Rectangle(this.bounds.right - this.opts.widthGrab + 1, 0, this.opts.widthGrab, this.parent.opts.height);
    this.opts.minTime = this.parent.pixelToTimestamp(pxLeft);
    this.opts.maxTime = this.parent.pixelToTimestamp(pxRight);

    this.parent.paint();
  }
}

class RangeSlider {
  initComplete = false;
  lastKeyTyped;
  mouse = { x: 0, y: 0 };
  isTrackingMouse = false;
  isHovered = false;

  constructor() {
    const chartStore = useChartStore();
    const { isLoggedIn } = useAuthentication();
    const { rangeStartDate, rangeEndDate } = storeToRefs(chartStore);

    this.onChangeInputs = this.onChangeInputs.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onContextMenu = this.onContextMenu.bind(this);

    this.opts = {
      width: isLoggedIn.value ? 360 : 300,
      height: this.isTouchDevice() ? 33 : 19,
      track: {
        height: 15,
        fill: 'rgba(240,240,240,.8)',
        border: 'black'
      },
      slider: {
        fill: 'rgba(240,240,240,.8)',
        border: 'black',
        hover: 'rgba(200,200,200,.8)',
        widthGrab: this.isTouchDevice() ? 20 : 10
      },
      arrows: {
        height: this.isTouchDevice() ? 33 : 15,
        fill: 'rgba(240,240,240,.8)',
        border: 'black',
        hover: '#c8c8c8'
      },
      menu: {
        fill: 'rgb(240,240,240)',
        border: 'black'
      }
    };

    this.rootHtmlElement = document.createElement('span');
    this.rootHtmlElement.style.display = 'inline-block';
    this.canvas = document.createElement('canvas');

    this.canvas.width = this.opts.width;
    this.canvas.height = this.opts.height;
    this.canvas.style.userSelect = 'none';
    this.ctx = this.canvas.getContext('2d');

    this.rootHtmlElement.appendChild(this.canvas);

    this.maxAllowableTime = new TimeStamp(MAX_RANGE_SLIDER_DATE);
    this.minAllowableTime = new TimeStamp(MIN_ALLOWED_DATE_STRING);

    const maxSliderTime = new TimeStamp(MAX_RANGE_SLIDER_DATE);
    const minSliderTime = maxSliderTime.subtract(1, 'year');
    const maxTrackTime = maxSliderTime.clone();
    const minTrackTime = new TimeStamp(maxSliderTime.getMillis() - 4 * (maxSliderTime.getMillis() - minSliderTime.getMillis()));

    this.leftArrow = new LeftArrow(this, {
      height: this.opts.arrows.height,
      colorFill: this.opts.arrows.fill,
      colorBorder: this.opts.arrows.border,
      colorHover: this.opts.arrows.hover
    });
    this.rightArrow = new RightArrow(this, {
      height: this.opts.arrows.height,
      colorFill: this.opts.arrows.fill,
      colorBorder: this.opts.arrows.border,
      colorHover: this.opts.arrows.hover
    });
    this.track = new Track(this, {
      height: this.opts.track.height,
      colorFill: this.opts.track.fill,
      colorBorder: this.opts.track.border,
      minTime: minTrackTime,
      maxTime: maxTrackTime
    });
    this.slider = new Slider(this, {
      colorFill: this.opts.slider.fill,
      colorBorder: this.opts.slider.border,
      colorHover: this.opts.slider.hover,
      widthGrab: this.opts.slider.widthGrab,
      minTime: minSliderTime,
      maxTime: maxSliderTime
    });

    watch(rangeStartDate, this.onChangeInputs);
    watch(rangeEndDate, this.onChangeInputs);

    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mouseover', this.onMouseOver);
    this.canvas.addEventListener('mouseout', this.onMouseOut);
    this.canvas.addEventListener('touchstart', this.onTouchStart);
    this.canvas.addEventListener('touchend', this.onTouchEnd);
    this.canvas.addEventListener('touchmove', this.onTouchMove);
    this.canvas.addEventListener('contextmenu', this.onContextMenu);

    window.addEventListener('mouseleave', this.onMouseLeave);

    document.body.addEventListener('keydown', this.onKeyDown);

    // we have instatiated the ui with default values
    // now take into account any start/end dates supplied in the input fields
    this.onChangeInputs();

    // render to the screen
    this.paint();

    this.initComplete = true;
  }

  paint() {
    this.ctx.clearRect(0, 0, this.opts.width, this.opts.height);
    this.track.paint();
    this.leftArrow.paint();
    this.rightArrow.paint();
    this.slider.paint();
  }

  isTouchDevice() {
    return navigator.userAgent.match(/iPad/i) != null;
  }

  isRangerVisible() {
    const chartStore = useChartStore();
    const { CHART_SETTINGS_MODE, currentModeId } = useModes();

    return (
      currentModeId.value === CHART_SETTINGS_MODE.id &&
      chartStore.shouldDisplayRangeDatePickers
    );
  }

  setDates(startDate, endDate) {
    const chartStore = useChartStore();

    if (!this.initComplete) {
      window.sc3WorkbenchLogger.warn(`Unable to set date range provided by RangeSlider - RangeSlider init process is not yet complete.`);
      return;
    }

    const startDateString = startDate.isToday() ? '' : startDate.format(DATE_FORMAT);
    const endDateString = endDate.isToday() ? '' : endDate.format(DATE_FORMAT);

    if (
      !isValidSharpCharts2DateString(startDateString) ||
      !isValidSharpCharts2DateString(endDateString)
    ) {
      window.sc3WorkbenchLogger.warn(`Unable to set date range provided by RangeSlider - RangeSlider provided in invalid startDateString (${typeof startDateString}|${startDateString ? 'truthy' : 'falsy'}: ${startDateString}) and/or endDateString (${typeof endDateString}|${endDateString ? 'truthy' : 'falsy'}: ${endDateString}).`);
      return;
    }

    if (
      isSharpCharts2DateStringOlderThanMinimumAllowed(startDateString) ||
      isSharpCharts2DateStringOlderThanMinimumAllowed(endDateString)
    ) {
      window.sc3WorkbenchLogger.warn(`Unable to set date range provided by RangeSlider - RangeSlider provided a startDateString: ${startDateString} and/or endDateString: ${endDateString} that is older than the minimum allowed.`);
      return;
    }

    chartStore.$patch({
      range: RANGE_OPTION_SELECT_START_END.title,
      rangeStartDate: startDateString,
      rangeEndDate: endDateString,
      isMakingEntireStartEndDateChange: true
    });
  }

  onChangeInputs() {
    if (!this.isRangerVisible() || this.slider.isDraggingOrExpanding()) {
      return;
    }

    const chartStore = useChartStore();
    const startTime = new TimeStamp(chartStore.rangeStartDate);
    const endTime = new TimeStamp(chartStore.rangeEndDate);
    const isBadStart = startTime.isNull || startTime.ge(endTime) || startTime.lt(this.minAllowableTime) || endTime.gt(this.maxAllowableTime);
    const isBadEnd = endTime.isNull || endTime.le(startTime);
    const isTabKey = this.lastKeyTyped === 'Tab';

    if (isBadStart || isBadEnd || isTabKey) {
      // do nothing, we never correct a value that the user has entered
      // also, we don't do anything if user presses tab (they are still editing)
      // do not attempt to paint the widget
      return;
    }

    pubsub.emit('change inputs', startTime, endTime);
  }

  onMouseLeave(e) {
    if (
      !this.isRangerVisible() ||
      e.target.nodeName.toLowerCase() !== 'html'
    ) {
      return;
    }

    // mouse has left the browser window, fire a mouseup
    this.onMouseUp();
  }

  onMouseDown(e) {
    if (!this.isRangerVisible() || this.isTouchDevice()) {
      return;
    }

    this.mouse = this.globalToLocal({
      x: e.pageX,
      y: e.pageY
    });

    // start listening for the mouseup event anywhere on the document
    document.body.addEventListener('mouseup', this.onMouseUp);

    pubsub.emit('mousedown', this.mouse);
  }

  onMouseUp() {
    // stop tracking the mouse if the cursor is outside the widget
    if (!this.isHovered) {
      this.stopTrackingMouse();
    }

    // stop listening for mouseup event
    document.body.removeEventListener('mouseup', this.onMouseUp);
    pubsub.emit('mouseup');
  }

  onMouseMove(e) {
    const mousePrev = this.mouse;
    this.mouse = this.globalToLocal({
      x: e.pageX,
      y: e.pageY
    });

    pubsub.emit('mousemove', mousePrev, this.mouse);
  }

  onMouseOver() {
    if (!this.isRangerVisible() || this.isTouchDevice()) {
      return;
    }

    this.isHovered = true;

    // when the cursor enters the widget, we start tracking the mouse
    if (!this.isTrackingMouse) {
      this.startTrackingMouse();
    }

    pubsub.emit('mouseover');
  }

  onMouseOut() {
    if (!this.isRangerVisible()) {
      return;
    }

    this.isHovered = false;

    // when the cursor leaves the widget, we stop tracking the mouse
    // unless we are in the process of dragging the slider
    if (!this.slider.isDraggingOrExpanding() && this.isTrackingMouse) {
      this.stopTrackingMouse();
    }

    pubsub.emit('mouseout');
  }

  onTouchStart(e) {
    if (!this.isRangerVisible()) {
      return;
    }

    this.mouse = this.globalToLocal({
      x: e.targetTouches[0].pageX,
      y: e.targetTouches[0].pageY
    });

    pubsub.emit('mousedown', this.mouse);
  }

  onTouchEnd() {
    if (!this.isRangerVisible()) {
      return;
    }

    pubsub.emit('mouseup');
  }

  onTouchMove(e) {
    if (!this.isRangerVisible()) {
      return;
    }

    const mousePrev = this.mouse;
    this.mouse = this.globalToLocal({
      x: e.changedTouches[0].pageX,
      y: e.changedTouches[0].pageY
    });

    pubsub.emit('mousemove', mousePrev, this.mouse);
  }

  onKeyDown(e) {
    const layoutStore = useLayoutStore();

    this.lastKeyTyped = e.key;

    if (
      this.isRangerVisible() &&
      !isAnHtmlUIElementFocused() &&
      !layoutStore.modalType
    ) {
      this.onKeyUp(e);
    }
  }

  onKeyUp(e) {
    const isLeftArrow  = e.key === 'ArrowLeft';
    const isRightArrow = e.key === 'ArrowRight';

    if (isLeftArrow) {
      pubsub.emit('nudge left');
    } else if (isRightArrow) {
      pubsub.emit('nudge right');
    }
  }

  onContextMenu(e) {
    e.preventDefault();

    if (!this.isRangerVisible()) {
      return;
    }

    pubsub.emit('contextmenu', this.mouse);
  }

  globalToLocal(coord) {
    // coord is event.pageX, event.pageY
    // used to convert document coordinates to widget coordinates
    const canvasBoundingClientRect = this.canvas.getBoundingClientRect();

    return {
      x: coord.x - canvasBoundingClientRect.left - window.scrollX,
      y: coord.y - canvasBoundingClientRect.top - window.scrollY
    };
  }

  localToGlobal(coord) {
    // convert widget coordinates to document coordinates
    // used for the context menu
    const canvasBoundingClientRect = this.canvas.getBoundingClientRect();

    return {
      x: coord.x + canvasBoundingClientRect.left + window.scrollX,
      y: coord.y + canvasBoundingClientRect.top + window.scrollY
    };
  }

  timestampToPixel(timestamp) {
    const val = timestamp.getMillis();
    const valMin = this.track.opts.minTime.getMillis();
    const valMax = this.track.opts.maxTime.getMillis();
    const fraction = (val - valMin) / (valMax - valMin);

    return this.track.bounds.left + (this.track.bounds.right - this.track.bounds.left) * fraction;
  }

  pixelToTimestamp(px) {
    const pxMin = this.track.bounds.left;
    const pxMax = this.track.bounds.right;
    const fraction = (px - pxMin) / (pxMax - pxMin);
    const millis = Math.round(this.track.opts.minTime.getMillis() + (this.track.opts.maxTime.getMillis() - this.track.opts.minTime.getMillis()) * fraction);

    return new TimeStamp(millis);
  }

  startTrackingMouse() {
    // we track the mouse anywhere on the page (not just over the widget)
    if (this.isTrackingMouse) {
      return;
    }

    document.body.addEventListener('mousemove', this.onMouseMove);

    this.isTrackingMouse = true;
  }

  stopTrackingMouse() {
    document.body.removeEventListener('mousemove', this.onMouseMove);

    this.isTrackingMouse = false;
  }
}

function createRangeSlider() {
  const rangeSlider = new RangeSlider();

  return rangeSlider.rootHtmlElement;
}

export function useRangeSlider() {
  return {
    createRangeSlider
  };
}
